/* eslint-disable indent */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { fetchFile } from '@ffmpeg/util';
import { AxiosResponse } from 'axios';
import { fabric } from 'fabric';
import { Form, Formik } from 'formik';

import { Input, SingleSelect } from '@/common';
import { ErrorPage } from '@/components/common/ErrorPage';
import {
  Breadcrumb,
  Button,
  Label,
  LABEL_TYPE,
  Spinner,
  ToggleSwitch,
  ValueSlider
} from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { TOGGLE_THEMES } from '@/components/generic/ToggleSwitch/toggle-switch-config';
import { queryClient } from '@/configuration/react-query';
import {
  BACKGROUND_OVERLAY,
  COVER_PHOTO,
  DEFAULT_OPACITY,
  DEFAULT_THUMBNAIL_EXTENSION,
  FULL_HD_DIMENSION,
  UPLOAD_PICKER_TYPES,
  VIDEO
} from '@/constants';
import { AssetScope, AssetType, CustomAttributeType, PRIVATE_PAGE_ROUTES } from '@/enums';
import { BlobLocation } from '@/enums/BlobLocationEnum';
import {
  calculateOpacity,
  checkIfAssetNeedsThumbnail,
  createImageFromCanvas,
  getAssetBlobClient,
  getAssetExtension,
  getAssetUrl,
  getCanvasJson,
  getProfileImageFromCanvas,
  getThumbnailFromType,
  getValidationFormatsFromPickerTypes,
  isSameUrlIgnoringSas,
  isVideoAsset,
  loadFonts,
  removeCoverImage,
  setBackgroundImageOpacity,
  setCanvasBackgroundImage,
  setCoverImage,
  uploadAsset,
  validateUploadFile
} from '@/helpers';
import { getFFmpeg } from '@/helpers/ffmpeg';
import { showToast, ToastType } from '@/helpers/toast';
import { AssetsModal } from '@/pages/Event/CoverPhotos/AssetsModal';
import { CoverPhotoAssetPanel } from '@/pages/Event/CoverPhotos/CoverPhotoAssetPanel';
import { dynamicValidationSchema } from '@/pages/Event/CoverPhotos/CoverPhotoValidation';
import { TextEditor } from '@/pages/Event/CoverPhotos/TextEditor';
import { Events as Calendar } from '@/resources';
import {
  useCreateAssetMutation,
  useCreateCoverPhotoMutation,
  useCreateSasTokenMutation,
  useGenerateCustomAttributesQuery,
  useGetCoverPhotoQuery,
  useGetCustomAttributeTemplatesByEventType,
  useGetEventAssets,
  useGetEventByIdQuery,
  useGetPublicAssets,
  useUpdateCoverPhoto
} from '@/services';
import { TestIds } from '@/shared/test-ids';
import { useCanvasStore } from '@/store/canvas';
import {
  Asset,
  CanvasField,
  CoverPhotoPayload,
  CreateEditAssetPayload,
  CustomAttributeTemplate,
  SasTokenPayload,
  SelectOptionProps
} from '@/types';

import { CoverPhotoCanvas } from './CoverPhotoCanvas';

type CreateEditCoverPhotosProps = {
  isMobileView?: boolean;
  isUploadPage?: boolean;
};

type InitialValues = {
  [key: string]: string;
};

type ModalState = {
  isModalOpen: boolean;
  isBackgroundAsset: boolean;
  usePublicAssets: boolean;
};

export type AppliedStylesType = {
  textAlign: string;
  fill: string;
  fontSize: string;
  fontFamily: string;
};

const DEFAULT_TEXT_BOX_STYLES = {
  fill: '#000000',
  fontFamily: 'Roboto Serif',
  fontSize: '60',
  textAlign: 'left'
};

const FIELD_MAX_LENGTH = 40;

export const CreateEditCoverPhotos = ({ isUploadPage = false }: CreateEditCoverPhotosProps) => {
  const { eventId, finalAssetId } = useParams();
  const isEditPage = !!finalAssetId;
  const { canvas, moveObjects } = useCanvasStore();
  const [t] = useTranslation('common');
  const navigate = useNavigate();
  const [profilePhoto, setProfilePhoto] = useState<Asset | null>(null);
  const [customBackgroundAsset, setCustomBackgroundAsset] = useState<Asset | null>(null);
  const [themeBackgroundAsset, setThemeBackgroundAsset] = useState<Asset | null>(null);
  const [modalState, setModalState] = useState<ModalState>({
    isModalOpen: false,
    isBackgroundAsset: false,
    usePublicAssets: false
  });
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [activeTextFiled, setActiveTextField] = useState<string>('');
  const [activeTextboxStyle, setActiveTextboxStyle] = useState<AppliedStylesType>({
    ...DEFAULT_TEXT_BOX_STYLES
  });
  const [, setUploading] = useState(false);
  const [isSubmittingCoverImage, setIsSubmittingCoverImage] = useState<boolean>(false);
  const [errorEvent, setErrorEvent] = useState<number>();
  const [errorCoverPhoto, setErrorCoverPhoto] = useState<number>();
  const [videoProcessing, setVideoProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [opacity, setOpacity] = useState<number>(DEFAULT_OPACITY);
  const [hasProfilePhotoBorder, setHasProfilePhotoBorder] = useState<boolean>(true);
  const [selectedTags, setSelectedTags] = useState<SelectOptionProps[]>();

  const coverPhotoValidationFormats = useMemo(
    () => getValidationFormatsFromPickerTypes(UPLOAD_PICKER_TYPES.IMAGE_VIDEO),
    []
  );

  const {
    data: eventData,
    isLoading: isLoadingEvent,
    isError: isErrorEvent
  } = useGetEventByIdQuery(Number(eventId), {
    onSuccess: () => undefined,
    onError: (error) => setErrorEvent(error.status)
  });

  const { data: allAssets, isLoading: isLoadingAllAssets } = useGetEventAssets(
    eventId ?? '',
    selectedTags?.map((tag: SelectOptionProps) => tag?.value as string) ?? [],
    0,
    undefined,
    [AssetType.IMAGE.toString(), AssetType.VIDEO.toString()],
    undefined,
    undefined,
    !!eventData
  );

  const { data: assetsPublic, isLoading: isLoadingAssets } = useGetPublicAssets([''], 0, true, '', [
    AssetType.COVER_PHOTO.toString()
  ]);

  const { data: generatedCustomAttributes, isLoading: isLoadingGeneratedCustomAttributes } =
    useGenerateCustomAttributesQuery(Number(eventId), !!eventData);

  const {
    data: coverPhoto,
    isLoading: isLoadingCoverPhoto,
    isError: isErrorCoverPhoto
  } = useGetCoverPhotoQuery(
    Number(eventId),
    Number(finalAssetId),
    {
      onSuccess: () => undefined,
      onError: (error) => setErrorCoverPhoto(error.status)
    },
    !!eventData
  );

  const { data: customAttributeTemplates, isLoading: isLoadingCustomAttributeTemplates } =
    useGetCustomAttributeTemplatesByEventType(
      eventData?.data.eventTypeId,
      CustomAttributeType.COVER_PHOTO
    );

  const { mutate: createCoverPhoto } = useCreateCoverPhotoMutation(queryClient, {
    onSuccess: (response?: AxiosResponse) => {
      setIsSubmittingCoverImage(false);
      if (isUploadPage) {
        navigate(PRIVATE_PAGE_ROUTES.COVER_PHOTOS.replace(':eventId', eventId ? eventId : ''));
      } else {
        navigate(PRIVATE_PAGE_ROUTES.SINGLE_EVENT.replace(':eventId', eventId ? eventId : ''));
      }
      showToast(
        ToastType.SUCCESS,
        t('assetSuccessfullyCreated', { assetName: response?.data.assetName })
      );
    },
    onError: () => {
      showToast(ToastType.ERROR, t('assetError'));
    }
  });

  const { mutate: updateCoverPhoto } = useUpdateCoverPhoto(queryClient, {
    onSuccess: () => {
      if (isUploadPage) {
        navigate(PRIVATE_PAGE_ROUTES.COVER_PHOTOS.replace(':eventId', eventId ? eventId : ''));
      } else {
        navigate(PRIVATE_PAGE_ROUTES.SINGLE_EVENT.replace(':eventId', eventId ? eventId : ''));
      }
      showToast(ToastType.SUCCESS, t('coverPhotoSuccessfullyUpdated'));
    },
    onError: () => {
      showToast(ToastType.ERROR, t('updateCoverPhotoError'));
    }
  });

  const { mutate: createAsset } = useCreateAssetMutation(queryClient, {
    onSuccess: (response?: AxiosResponse) => {
      if (response) {
        if (coverPhoto?.data?.id) {
          updateCoverPhoto({
            ...getCoverPhotoPayload(),
            coverPhotoAssetId: response.data.id,
            finalAssetId: response.data.id,
            coverPhotoJson: getCanvasJson(canvas)
          });
        } else {
          createCoverPhoto({
            ...getCoverPhotoPayload(),
            coverPhotoAssetId: response.data.id,
            finalAssetId: response.data.id,
            coverPhotoJson: getCanvasJson(canvas)
          });
        }
      }
    },
    onError: () => {
      setIsSubmittingCoverImage(false);
      showToast(ToastType.ERROR, t('assetError'));
    }
  });

  const { mutateAsync: createSasToken } = useCreateSasTokenMutation(queryClient, {
    onSuccess: () => {
      // eslint-disable-next-line no-console
      console.log(t('sasTokenSuccessfullyCreated'));
    },
    onError: () => {
      showToast(ToastType.ERROR, t('sasError'));
    }
  });

  const assets = useMemo(() => {
    const assetsList = allAssets?.pages.flatMap((page) => page.data?.items) || [];
    return assetsList;
  }, [allAssets]);

  const publicAssets = useMemo(() => {
    return assetsPublic?.pages.flatMap((page) => page.data?.items) || [];
  }, [assetsPublic]);

  useEffect(() => {
    if (coverPhoto && coverPhoto.data.eventId !== Number(eventId)) {
      navigate(PRIVATE_PAGE_ROUTES.SINGLE_EVENT.replace(':eventId', eventId ? eventId : ''));
    }
  }, [coverPhoto]);

  useEffect(() => {
    if (canvas && canvas?.getContext()) {
      handleInitialProfilePictureValue();
      handleInitialBackgroundAsset();
    }
  }, [canvas]);

  useEffect(() => {
    loadFonts();
  }, []);

  const handleInitialBackgroundAsset = () => {
    if (!canvas?.backgroundImage && publicAssets && !coverPhoto?.data?.backgroundAsset) {
      handleBackgroundAssetChange({ value: publicAssets[0]?.assetUrl ?? '', label: '' });
      return;
    }

    if (isVideoAsset(coverPhoto?.data?.backgroundAsset)) {
      setCustomBackgroundAsset(coverPhoto?.data?.backgroundAsset);
      canvas?.setBackgroundImage(null as unknown as fabric.Image, () => {
        canvas.requestRenderAll();
      });
      return;
    }

    const isCanvasBackground = canvas?.backgroundImage;

    const backgroundURL = isCanvasBackground
      ? (canvas?.backgroundImage as fabric.Image).getSrc()
      : coverPhoto?.data?.backgroundAsset.assetUrl;

    if (!isSameUrlIgnoringSas(coverPhoto?.data?.backgroundAsset?.assetUrl, backgroundURL)) {
      handleBackgroundAssetDelete();
      return;
    }

    const themeBackground = publicAssets.find((item) =>
      isSameUrlIgnoringSas(item.assetUrl, backgroundURL)
    ) as Asset;
    if (themeBackground) {
      isCanvasBackground
        ? setThemeBackgroundAsset(themeBackground)
        : handleBackgroundAssetChange({ value: themeBackground.assetUrl, label: '' });
      setCustomBackgroundAsset(themeBackground);
      return;
    }

    const backgroundAsset = assets.find((item) =>
      isSameUrlIgnoringSas(item.assetUrl, backgroundURL)
    ) as Asset;
    if (backgroundAsset) {
      isCanvasBackground
        ? setCustomBackgroundAsset(backgroundAsset)
        : handleBackgroundAssetUpload(backgroundAsset);
      return;
    }
  };

  const handleInitialProfilePictureValue = () => {
    const profileImage = getProfileImageFromCanvas(canvas as fabric.Canvas);
    if (profileImage) {
      const profileAsset = assets?.find((item) =>
        isSameUrlIgnoringSas(item.assetUrl, (profileImage as fabric.Image).getSrc())
      ) as Asset;
      profileAsset && setProfilePhoto(profileAsset);
      setHasProfilePhotoBorder(!!profileImage.stroke);
      if (profileAsset === undefined) {
        handleCoverPhotoDelete();
      }
      return;
    }
    if (coverPhoto?.data?.profilePhoto) {
      handleCoverImageUpload(coverPhoto.data.profilePhoto);
    }
  };

  const getCoverPhotoPayload = () => {
    return {
      id: coverPhoto?.data?.id ?? undefined,
      eventId: Number(eventId),
      coverPhotoAssetId: finalAssetId ? Number(finalAssetId) : null,
      profilePhotoId: Number(profilePhoto?.id) || null,
      backgroundAssetId:
        Number(customBackgroundAsset?.id) || themeBackgroundAsset?.id || publicAssets[0].id,
      finalAssetId: finalAssetId ? Number(finalAssetId) : null,
      customAttributeValues: []
    };
  };

  const getInitialFormValues = () => {
    if (isEditPage) {
      return getInitialValuesFromCanvas();
    } else {
      return getInitialValuesForNewForm();
    }
  };

  const getInitialValuesFromCanvas = () => {
    const canvasFields = canvas
      ?.getObjects('textbox')
      .reduce((acc: { [key: number]: string }, textbox) => {
        const fieldId = textbox.data.id;
        const fieldValue = (textbox as fabric.Textbox).text ?? '';
        acc[fieldId] = fieldValue;
        return acc;
      }, {});

    if (!Object.keys(canvasFields ?? {}).length) {
      return getInitialValuesForNewForm();
    }

    return canvasFields;
  };

  const getInitialValuesForNewForm = (): InitialValues => {
    const initialValues: InitialValues = {};

    if (generatedCustomAttributes?.data) {
      generatedCustomAttributes.data.forEach(
        (attribute: { customAttributeTemplateId: number; value: string }) => {
          initialValues[attribute.customAttributeTemplateId] = attribute.value ?? '';
        }
      );
    }

    return initialValues;
  };

  const getBackgroundDefaultTheme = (url: string) => {
    return publicAssets.find((item) => item.assetUrl === url) as Asset;
  };

  const getBackgroundData = () => {
    if (customBackgroundAsset && customBackgroundAsset.id !== themeBackgroundAsset?.id) {
      return { value: 'custom', label: t('custom') } as SelectOptionProps;
    }
    if (themeBackgroundAsset) {
      return { value: themeBackgroundAsset.assetUrl, label: themeBackgroundAsset.assetName };
    }
  };

  const titleAttribute = customAttributeTemplates?.data.find(
    (attr: { labelKey: string }) => attr.labelKey === 'title'
  );

  const getCanvasFields = (values: Record<string, string | number>) => {
    if (!values) {
      return;
    }

    if (titleAttribute) {
      return Object.keys(values)
        .sort((a, b) =>
          a === String(titleAttribute.id) ? -1 : b === String(titleAttribute.id) ? 1 : 0
        )
        .map((key) => ({
          id: Number(key),
          label: key,
          value: values[key]
        })) as CanvasField[];
    }
  };

  const handleOpenModal = (isBackgroundAsset: boolean, usePublicAssets = false) => {
    setModalState((prevState) => ({
      ...prevState,
      isModalOpen: true,
      isBackgroundAsset: isBackgroundAsset,
      usePublicAssets: usePublicAssets
    }));
  };

  const onCloseModal = () => {
    setModalState((prevState) => ({
      ...prevState,
      isModalOpen: false,
      isBackgroundAsset: false,
      usePublicAssets: false
    }));
  };

  const handleSubmitModal = () => {
    if (modalState.isBackgroundAsset) {
      if (isVideoAsset(selectedAsset)) {
        canvas?.setBackgroundImage(null as unknown as fabric.Image, () => {
          canvas.requestRenderAll();
        });
      } else {
        setCanvasBackgroundImage(canvas as fabric.Canvas, selectedAsset?.assetUrl as string);
      }
      setCustomBackgroundAsset(selectedAsset);
      setBackgroundImageOpacity(canvas as fabric.Canvas, false, 0);
      setOpacity(DEFAULT_OPACITY);
    } else {
      setCoverImage(
        canvas as fabric.Canvas,
        selectedAsset?.assetUrl as string,
        moveObjects,
        hasProfilePhotoBorder
      );
      setProfilePhoto(selectedAsset);
    }

    setModalState((prevState) => ({
      ...prevState,
      isModalOpen: false,
      isBackgroundAsset: false,
      usePublicAssets: false
    }));
  };

  const closeTextEditor = () => {
    setActiveTextField('');
  };

  const openTextEditor = (textboxId: string) => {
    const textbox = canvas?.getObjects('textbox').find((textbox) => textbox.data.id === textboxId);
    if (!textbox) {
      return;
    }
    const textboxStyle = {
      fill: textbox.fill as string,
      fontFamily: (textbox as fabric.Textbox).fontFamily as string,
      fontSize: (textbox as fabric.Textbox).fontSize?.toString() as string,
      textAlign: (textbox as fabric.Textbox).textAlign as string
    };

    setActiveTextboxStyle(textboxStyle);
    canvas?.setActiveObject(textbox as fabric.Object);
    canvas?.requestRenderAll();
    setActiveTextField(textboxId);
  };

  const handleTextboxStyle = (textboxId: string, propertyName: string, value: string) => {
    const textbox = canvas?.getObjects('textbox').find((textbox) => textbox.data.id === textboxId);

    if (!textbox) {
      return;
    }

    (textbox as fabric.Textbox).set(propertyName as keyof fabric.Textbox, value);
    setActiveTextboxStyle({ ...activeTextboxStyle, [propertyName]: value });
    canvas?.renderAll();
  };

  const handleFieldTextChange = (textboxId: string, value: string) => {
    const textbox = canvas?.getObjects('textbox').find((textbox) => textbox.data.id === textboxId);

    (textbox as fabric.Textbox).set({
      text: value
    });

    canvas?.requestRenderAll();
  };

  const handleBackgroundAssetChange = (value: SelectOptionProps) => {
    setBackgroundImageOpacity(canvas as fabric.Canvas, false, 0);
    setOpacity(DEFAULT_OPACITY);
    const publicBackgroundAsset = getBackgroundDefaultTheme(value?.value as string);
    setThemeBackgroundAsset(publicBackgroundAsset);

    if (isVideoAsset(publicBackgroundAsset)) {
      handleBackgroundAssetUpload(publicBackgroundAsset);
      return;
    }
    setCustomBackgroundAsset(null);
    setCanvasBackgroundImage(canvas as fabric.Canvas, value?.value as string);
  };

  const handleBackgroundAssetUpload = (asset: Asset) => {
    if (isVideoAsset(asset)) {
      canvas?.setBackgroundImage(null as unknown as fabric.Image, () => {
        canvas.requestRenderAll();
      });
    } else {
      setCanvasBackgroundImage(canvas as fabric.Canvas, asset.assetUrl);
    }
    setCustomBackgroundAsset(asset);
    setSelectedAsset(null);
  };

  const handleCoverImageUpload = (asset: Asset) => {
    setCoverImage(
      canvas as fabric.Canvas,
      asset.assetUrl as string,
      moveObjects,
      hasProfilePhotoBorder
    );
    setProfilePhoto(asset);
  };

  const handleCoverPhotoDelete = () => {
    removeCoverImage(canvas as fabric.Canvas);
    setProfilePhoto(null);
  };

  const handleBackgroundAssetDelete = () => {
    setThemeBackgroundAsset(publicAssets[0]);
    setCanvasBackgroundImage(canvas as fabric.Canvas, publicAssets[0].assetUrl);
    setCustomBackgroundAsset(null);
  };

  const generateImageName = (fileExtension: string) => {
    const timestamp = new Date().getTime();
    const prefix = t('coverPhoto');
    const normalizedName = prefix.trim().replace(/\s+/g, '-');
    return `${normalizedName}-${timestamp}.${fileExtension}`;
  };

  const handleUploadAsset = async (uploadedAsset: File) => {
    const assetType = AssetType.COVER_PHOTO;

    const sasToken: SasTokenPayload = {
      id: Number(eventId),
      isPublic: false,
      isThumbnail: false,
      assetName: '',
      extension: getAssetExtension(uploadedAsset.name),
      blobLocation: BlobLocation.EVENTS
    };

    const data = await createSasToken(sasToken);

    if (!data) {
      setIsSubmittingCoverImage(false);
      return;
    }

    const assetBlobClient = getAssetBlobClient(data);

    if (!(await uploadAsset(assetBlobClient, uploadedAsset, setUploading))) {
      setIsSubmittingCoverImage(false);
      return;
    }

    const asset: CreateEditAssetPayload = {
      eventId: Number(eventId),
      organizationId: eventData?.data?.organization?.id,
      assetType: assetType,
      assetScope: AssetScope.PRIVATE,
      assetName: uploadedAsset.name,
      assetUrl: getAssetUrl(data),
      assetSize: Number(500000),
      thumbnailUrl: '',
      ...{
        tags: [
          {
            name: COVER_PHOTO
          }
        ]
      }
    };

    if (!checkIfAssetNeedsThumbnail(assetType)) {
      createAsset(asset);
      setUploading(false);
      setIsSubmittingCoverImage(false);
      return;
    }

    if (customBackgroundAsset && isVideoAsset(customBackgroundAsset)) {
      setCanvasBackgroundImage(
        canvas as fabric.Canvas,
        customBackgroundAsset.thumbnailUrl,
        async () => {
          const coverImageSrc = createImageFromCanvas(canvas as fabric.Canvas, 'png');
          canvas?.setBackgroundImage(null as unknown as fabric.Image, () => undefined);
          const uploadedAssetVideo = await createImageAsset(coverImageSrc as string, 'png');
          await handleUploadThumbnail(uploadedAssetVideo, data, sasToken, asset);
        }
      );
    } else {
      await handleUploadThumbnail(uploadedAsset, data, sasToken, asset);
    }
  };

  const handleUploadThumbnail = async (
    uploadedAsset: File,
    data: AxiosResponse<{ assetName: string }>,
    sasToken: SasTokenPayload,
    asset: CreateEditAssetPayload
  ) => {
    const assetName = data.data.assetName;

    const thumbnail = await getThumbnailFromType(uploadedAsset);
    if (!thumbnail) {
      setIsSubmittingCoverImage(false);
      return;
    }
    sasToken.isThumbnail = true;
    sasToken.assetName = assetName;
    sasToken.extension = DEFAULT_THUMBNAIL_EXTENSION;
    data = await createSasToken(sasToken);

    if (!data) {
      setIsSubmittingCoverImage(false);
      return;
    }

    const thumbnailBlobClient = getAssetBlobClient(data);
    if (!(await uploadAsset(thumbnailBlobClient, thumbnail.file, setUploading))) {
      setIsSubmittingCoverImage(false);
      return;
    }

    asset.thumbnailUrl = getAssetUrl(data);

    createAsset(asset);
    setUploading(false);
  };

  const processVideoWithFFmpeg = async () => {
    if (!canvas) {
      setVideoProcessing(false);
      return;
    }
    setVideoProcessing(true);
    setProgress(0);

    const ffmpeg = await getFFmpeg((progressValue) => {
      if (progressValue >= 0 && progressValue <= 1) {
        setProgress(progressValue);
      } else if (progressValue > 1 && progressValue <= 100) {
        setProgress(progressValue / 100);
      }
    });

    const originalBackgroundColor = canvas.backgroundColor;
    const overlaySrc = await new Promise<string | null>((resolve) => {
      canvas.setBackgroundImage(null as unknown as fabric.Image, () => {
        canvas.backgroundColor = 'transparent';
        canvas.requestRenderAll();
        const src = createImageFromCanvas(canvas, 'png');
        canvas.backgroundColor = originalBackgroundColor;
        customBackgroundAsset && handleBackgroundAssetUpload(customBackgroundAsset);
        canvas.requestRenderAll();
        resolve(src);
      });
    });

    if (!overlaySrc) {
      setVideoProcessing(false);
      return;
    }

    const overlayImage = await createImageAsset(overlaySrc, 'png');
    const videoFile = await fetchFile(customBackgroundAsset?.assetUrl);

    await ffmpeg.writeFile('background.mp4', videoFile);
    await ffmpeg.writeFile('overlay.png', await fetchFile(overlayImage));

    await ffmpeg.exec([
      '-i',
      'background.mp4',
      '-i',
      'overlay.png',
      '-preset',
      'ultrafast',
      '-filter_complex',
      `[0:v]scale=${FULL_HD_DIMENSION.WIDTH}:${FULL_HD_DIMENSION.HEIGHT}:force_original_aspect_ratio=decrease,pad=${FULL_HD_DIMENSION.WIDTH}:${FULL_HD_DIMENSION.HEIGHT}:(ow-iw)/2:(oh-ih)/2:white,format=yuv420p[v0];[1:v]format=rgba,colorchannelmixer=aa=1[v1];[v0][v1]overlay=0:0`,
      '-an',
      'output.mp4'
    ]);

    const data = await ffmpeg.readFile('output.mp4');
    const processedVideoBlob = new Blob([data as BlobPart], { type: 'video/mp4' });
    const processedVideoFile = new File([processedVideoBlob], generateImageName('mp4'), {
      type: 'video/mp4'
    });

    await handleUploadAsset(processedVideoFile);
    setVideoProcessing(false);
    setProgress(0);
  };

  const handleCoverImageSubmit = async () => {
    if (customBackgroundAsset && isVideoAsset(customBackgroundAsset)) {
      await processVideoWithFFmpeg();
      return;
    }
    setIsSubmittingCoverImage(true);
    const coverImageFormat = 'png';
    const coverImageSrc = createImageFromCanvas(canvas as fabric.Canvas, coverImageFormat);
    const assetForUpload = await createImageAsset(coverImageSrc as string, coverImageFormat);
    if (!coverImageSrc) {
      setIsSubmittingCoverImage(false);
      return;
    }
    const validationResult = validateUploadFile({
      file: assetForUpload,
      validationFormats: coverPhotoValidationFormats
    });

    if (validationResult.errorType) {
      setIsSubmittingCoverImage(false);
      showToast(ToastType.ERROR, t('unsupportedMediaFormat'));
      return;
    }
    await handleUploadAsset(assetForUpload);
  };

  const createImageAsset = (src: string, format: string): Promise<File> => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.src = src;

      image.onload = async () => {
        const blob = await fetch(image.src).then((response) => response.blob());
        const asset = new File([blob], generateImageName(format), { type: `image/${format}` });
        resolve(asset);
      };

      image.onerror = (error) => {
        reject(error);
      };
    });
  };

  const getAssetsForModal = (assets: Asset[], isBackgroundAsset: boolean): Asset[] => {
    return assets.filter((asset) => {
      if (isBackgroundAsset) {
        return true;
      }
      return asset.assetType !== VIDEO;
    });
  };

  const isObjectBackgroundOverlay = (object: fabric.Object) => {
    return object?.data?.id === BACKGROUND_OVERLAY;
  };

  const enableMoveObjects = (moveObjects: boolean) => {
    if (!canvas || canvas.getContext() === null) {
      return;
    }

    canvas.forEachObject((obj) => {
      const isObjOverlay = isObjectBackgroundOverlay(obj);
      obj.set({
        selectable: !isObjOverlay && moveObjects,
        evented: !isObjOverlay && moveObjects,
        hasControls: !isObjOverlay && moveObjects,
        lockMovementX: isObjOverlay || !moveObjects,
        lockMovementY: isObjOverlay || !moveObjects,
        lockScalingX: isObjOverlay || !moveObjects,
        lockScalingY: isObjOverlay || !moveObjects,
        lockRotation: isObjOverlay || !moveObjects,
        hasBorders: !isObjOverlay && moveObjects
      });
    });

    canvas.renderAll();
  };

  const handleOpacity = (opacity: number) => {
    setOpacity(opacity);

    if (canvas && opacity !== undefined) {
      const lighten = opacity > 0;
      const opacityValue = Math.abs(opacity) / 100;
      setBackgroundImageOpacity(canvas as fabric.Canvas, lighten, opacityValue);
    }
  };

  useEffect(() => {
    enableMoveObjects(moveObjects);
  }, [moveObjects]);

  useEffect(() => {
    if (!coverPhoto?.data?.coverPhotoJson) {
      return;
    }

    const parsedJsonObject = JSON.parse(coverPhoto.data.coverPhotoJson);

    const overlay = parsedJsonObject.objects?.find(
      (obj: fabric.Rect) => obj?.data?.id === BACKGROUND_OVERLAY
    );

    if (overlay) {
      setOpacity(calculateOpacity(overlay));
    } else {
      setOpacity(DEFAULT_OPACITY);
    }
  }, [coverPhoto]);

  const syncColors = (text: string) => {
    if (customAttributeTemplates) {
      customAttributeTemplates.data.forEach((customAttribute: CustomAttributeTemplate) => {
        handleTextboxStyle(customAttribute.id.toString(), 'fill', text);
      });
    }
  };

  const handleToggleProfilePictureBorder = () => {
    const profileImage = canvas?.getObjects().find((obj) => obj.data?.id === 'profileImage');

    if (!profileImage) {
      throw new Error('Profile image not found on canvas');
    }

    const center = profileImage.getCenterPoint();

    setHasProfilePhotoBorder(!hasProfilePhotoBorder);

    profileImage.set({
      strokeUniform: true,
      strokeWidth: hasProfilePhotoBorder ? 0 : 20,
      stroke: hasProfilePhotoBorder ? undefined : '#FFFFFF'
    });

    profileImage.setPositionByOrigin(center, 'center', 'center');

    canvas?.requestRenderAll();
  };

  return (
    <>
      {isLoadingEvent ||
      isLoadingAssets ||
      (!isErrorEvent &&
        ((isEditPage && isLoadingCoverPhoto) ||
          isLoadingCustomAttributeTemplates ||
          isLoadingGeneratedCustomAttributes ||
          isLoadingAllAssets)) ? (
        <div className='flex h-screen items-center justify-center'>
          <Spinner />
        </div>
      ) : errorCoverPhoto || errorEvent ? (
        <ErrorPage code={Number(errorEvent ?? errorCoverPhoto)} />
      ) : (
        !isErrorCoverPhoto &&
        !isErrorEvent && (
          <>
            {!isUploadPage && (
              <Breadcrumb
                icon={<Calendar className='stroke-[0.5] text-lg text-primary-blue' />}
                iconPath={'/events'}
                path={[
                  { label: eventData?.data?.name, url: `/events/${eventData?.data?.id}` },
                  { label: t('coverPhotos'), url: `/events/${eventData?.data?.id}` },
                  { label: isEditPage ? t('editCoverPhotos') : t('addCoverPhotos') }
                ]}
                className='mx-20 mb-9 mt-7 hidden md:block'
              />
            )}
            {videoProcessing && (
              <div className='pointer-events-auto absolute inset-0 z-20 flex items-center justify-center bg-transparent'></div>
            )}
            <div
              className={`w-full md:overflow-y-scroll md:scrollbar-none ${
                !isUploadPage && 'overflow-y-scroll px-3 py-4 md:p-0'
              }`}>
              <div className='flex flex-col md:mx-20 md:mb-9'>
                <div className='flex flex-col md:space-y-6'>
                  <Label
                    text={isEditPage ? t('editCoverPhoto') : t('createCoverPhoto')}
                    type={LABEL_TYPE.H2}
                    className='hidden md:block'
                  />
                  <Label
                    text={isEditPage ? t('coverPhotoWillBeUsed') : t('pleaseCreateCoverPhoto')}
                    type={LABEL_TYPE.PARAGRAPH2}
                    className='font-normal text-secondary-gray md:hidden md:font-semibold'
                  />
                  <Label
                    text={isEditPage ? t('coverPhotoWillBeUsed') : t('pleaseCreateCoverPhoto')}
                    type={LABEL_TYPE.PARAGRAPH1}
                    className='hidden font-normal text-secondary-gray md:block md:font-semibold'
                  />
                </div>
                <div className='mt-6 rounded-xl border bg-primary-gray md:hidden'>
                  <CoverPhotoAssetPanel
                    eventId={eventId ?? ''}
                    organizationId={eventData?.data?.organization?.id}
                    title='coverPhoto'
                    uploadText='uploadDragDropPortraitPhoto'
                    buttonText='uploadNewPhoto'
                    asset={profilePhoto}
                    isMobileView={true}
                    assetTag={COVER_PHOTO}
                    handleDeleteAsset={handleCoverPhotoDelete}
                    handleCreatedAsset={(asset: Asset) => handleCoverImageUpload(asset)}
                    handleOpenModal={() => handleOpenModal(false)}
                    disableButton={!assets.length}
                  />
                </div>
                <Formik
                  enableReinitialize
                  initialValues={getInitialFormValues() as { [key: number | string]: string }}
                  validationSchema={dynamicValidationSchema(customAttributeTemplates?.data)}
                  onSubmit={() => handleCoverImageSubmit()}>
                  {({ setFieldValue, values, errors, isSubmitting }) => (
                    <Form
                      id='createCoverPhoto'
                      data-testid={TestIds.coverPhotos.form}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                        }
                      }}>
                      <div className='mt-6 flex size-full flex-row gap-x-14 md:mt-9'>
                        <div className='flex w-full flex-col md:w-[70%]'>
                          <div>
                            <CoverPhotoCanvas
                              fields={getCanvasFields(values) as CanvasField[]}
                              containerClasses='!rounded-[10px] !border !border-secondary-pale-blue-gray w-full'
                              canvasClasses='!rounded-[10px]'
                              coverPhoto={coverPhoto?.data as CoverPhotoPayload}
                              video={customBackgroundAsset}
                              backgroundAsset={coverPhoto?.data.backgroundAsset}
                              profilePhoto={coverPhoto?.data.profilePhoto}
                            />
                          </div>
                          <div className='mt-6 rounded-xl border bg-primary-gray md:hidden'>
                            <CoverPhotoAssetPanel
                              eventId={eventId ?? ''}
                              organizationId={eventData?.data?.organization?.id}
                              title='background'
                              uploadText='uploadDragDropCustomBackground'
                              buttonText='uploadNewBackground'
                              asset={customBackgroundAsset}
                              isMobileView={true}
                              assetTag={COVER_PHOTO}
                              handleDeleteAsset={handleBackgroundAssetDelete}
                              handleCreatedAsset={(asset: Asset) =>
                                handleBackgroundAssetUpload(asset)
                              }
                              handleOpenModal={() => handleOpenModal(true)}
                              disableButton={!assets.length}
                            />
                            <div className='m-3' data-testid={TestIds.coverPhotos.backgroundSelect}>
                              <SingleSelect
                                id='background'
                                label={t('background')}
                                options={
                                  publicAssets.map((item) => ({
                                    value: item.assetUrl,
                                    label: item.assetName
                                  })) as SelectOptionProps[]
                                }
                                handleChange={(value) => {
                                  handleBackgroundAssetChange(value as SelectOptionProps);
                                  setFieldValue('background', value as SelectOptionProps);
                                }}
                                value={getBackgroundData()}
                                defaultValue={
                                  publicAssets.map((item) => ({
                                    value: item.assetUrl,
                                    label: item.assetName
                                  }))[0] as SelectOptionProps
                                }
                                containerClassName='mt-1'
                              />
                            </div>
                            {moveObjects && (
                              <div className='m-3'>
                                <Label
                                  type={LABEL_TYPE.PARAGRAPH2}
                                  text={t('exposure')}
                                  className='mb-3'
                                />
                                <ValueSlider
                                  value={opacity}
                                  onChange={handleOpacity}
                                  testId={TestIds.coverPhotos.exposureSlider}
                                />
                              </div>
                            )}
                          </div>
                          <div className='relative mb-5 mt-9 w-full md:grid md:grid-cols-2 md:gap-x-9 md:gap-y-2.5'>
                            {customAttributeTemplates?.data.map(
                              (customAttribute: CustomAttributeTemplate) => (
                                <div className='relative' key={customAttribute.id}>
                                  <Input
                                    type='text'
                                    key={customAttribute.id}
                                    id={customAttribute.id.toString()}
                                    label={t(customAttribute.labelKey)}
                                    errorMessage={errors[customAttribute.id]}
                                    isInvalid={!!errors[customAttribute.id]}
                                    hasIcon
                                    value={values[customAttribute.id]}
                                    handleChange={(text) => {
                                      setFieldValue(customAttribute.id.toString(), text);
                                      handleFieldTextChange(customAttribute.id.toString(), text);
                                    }}
                                    handleFocus={() =>
                                      openTextEditor(customAttribute.id.toString())
                                    }
                                    isTextEditorOpened={
                                      activeTextFiled === customAttribute.id.toString()
                                    }
                                    maxLength={FIELD_MAX_LENGTH}
                                  />
                                  {activeTextFiled === customAttribute.id.toString() && (
                                    <TextEditor
                                      closeTextEditor={() => closeTextEditor()}
                                      appliedStyles={activeTextboxStyle}
                                      handleTextStyleChange={(value, property) =>
                                        handleTextboxStyle(
                                          customAttribute.id.toString(),
                                          property,
                                          value
                                        )
                                      }
                                      syncColors={syncColors}
                                    />
                                  )}
                                </div>
                              )
                            )}
                          </div>
                          <Button
                            type='submit'
                            variant={ButtonVariant.PRIMARY}
                            disabled={isSubmitting || isSubmittingCoverImage || videoProcessing}
                            data-testid={TestIds.coverPhotos.saveButton}
                            className='my-6 w-full md:hidden'>
                            {videoProcessing ? `${Math.round(progress * 100)}%` : t('save')}
                          </Button>
                        </div>
                        <div className='mb-5 hidden w-[30%] flex-col rounded-xl border bg-primary-gray md:flex'>
                          <div className='space-y-6'>
                            <CoverPhotoAssetPanel
                              eventId={eventId ?? ''}
                              organizationId={eventData?.data?.organization?.id}
                              title='coverPhoto'
                              uploadText='uploadDragDropPortraitPhoto'
                              buttonText='uploadNewPhoto'
                              asset={profilePhoto}
                              assetTag={COVER_PHOTO}
                              handleDeleteAsset={handleCoverPhotoDelete}
                              handleCreatedAsset={(asset: Asset) => handleCoverImageUpload(asset)}
                              handleOpenModal={() => handleOpenModal(false)}
                              disableButton={!assets.length}
                            />
                            {profilePhoto && moveObjects && (
                              <div className='flex items-center gap-4 px-6'>
                                <ToggleSwitch
                                  data-testid={TestIds.coverPhotos.profileBorderToggle}
                                  handleToggle={() => {
                                    handleToggleProfilePictureBorder();
                                    setHasProfilePhotoBorder(!hasProfilePhotoBorder);
                                  }}
                                  toggled={hasProfilePhotoBorder}
                                  variant={TOGGLE_THEMES.DARK}
                                />
                                <Label
                                  text={t('showBorderAroundImage')}
                                  type={LABEL_TYPE.PARAGRAPH2}
                                />
                              </div>
                            )}
                            <div className='mx-6 h-px bg-secondary-blue-gray' />
                            <CoverPhotoAssetPanel
                              eventId={eventId ?? ''}
                              organizationId={eventData?.data?.organization?.id}
                              title='background'
                              uploadText='uploadDragDropCustomBackground'
                              buttonText='uploadNewBackground'
                              asset={customBackgroundAsset}
                              assetTag={COVER_PHOTO}
                              isBackgroundAsset={true}
                              handleDeleteAsset={handleBackgroundAssetDelete}
                              handleCreatedAsset={(asset: Asset) =>
                                handleBackgroundAssetUpload(asset)
                              }
                              handleOpenModal={(usePublicAssets: boolean) =>
                                handleOpenModal(true, usePublicAssets)
                              }
                              disableButton={!assets.length}
                            />
                            {moveObjects && (
                              <div className='relative ml-6 w-[calc(100%-3rem)] pb-1'>
                                <Label
                                  type={LABEL_TYPE.PARAGRAPH2}
                                  text={t('exposure')}
                                  className='mb-3'
                                />
                                <ValueSlider
                                  value={opacity}
                                  onChange={handleOpacity}
                                  testId={TestIds.coverPhotos.exposureSlider}
                                />
                              </div>
                            )}
                          </div>
                          <div className='my-6 flex w-full'>
                            <Button
                              type='submit'
                              variant={ButtonVariant.PRIMARY}
                              disabled={isSubmitting || isSubmittingCoverImage || videoProcessing}
                              data-testid={TestIds.coverPhotos.saveButton}
                              className='mx-6 w-full'>
                              {videoProcessing ? `${Math.round(progress * 100)}%` : t('save')}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
            {modalState.isModalOpen && (
              <AssetsModal
                isOpen={modalState.isModalOpen}
                usePublicAssets={modalState.usePublicAssets}
                isBackgroundAsset={modalState.isBackgroundAsset}
                assets={modalState.usePublicAssets ? publicAssets : assets}
                selectedAsset={selectedAsset}
                setSelectedAsset={setSelectedAsset}
                onClose={onCloseModal}
                onSubmit={handleSubmitModal}
                getAssetsForModal={getAssetsForModal}
                setSelectedTags={setSelectedTags}
                selectedTags={selectedTags}
              />
            )}
          </>
        )
      )}
    </>
  );
};
